'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { WaitlistSuccessCard } from '@/features/landing-page/components/WaitlistSuccessCard';
import type { Milestone } from '@/features/projects/types';

/**
 * Optional interactive mode props — when provided, the template renders
 * controlled inputs with form submission, validation, and success state.
 * When omitted (default), the template renders as a static display-only preview.
 */
export interface WidgetFormSuccessData {
    position: number;
    totalWaitlist: number;
    referralLink: string;
    referralCode: string;
    referralsCount: number;
    gamificationEnabled: boolean;
    milestones: Milestone[];
}

export interface WidgetFormInteractiveProps {
    values: Record<string, string>;
    onFieldChange: (field: string, value: string) => void;
    onFormSubmit: (e: React.FormEvent) => void;
    submitting: boolean;
    submitted: boolean;
    fieldErrors: Record<string, string>;
    error: string;
    successMessage?: string;
    hideChrome?: boolean;
    successData?: WidgetFormSuccessData | null;
    showQueuePosition?: boolean;
    showReferralAfterSubmit?: boolean;
    primaryColor?: string;
}

/**
 * Shared props for all widget form template previews.
 * These are the live-editing values from the customization panel.
 */
export interface WidgetFormTemplateProps {
    primaryColor: string;
    buttonText: string;
    emailPrompt: string;
    collectFirstName: boolean;
    collectLastName: boolean;
    collectPhone: boolean;
    customQuestion: string | null;
    successMessage: string | null;
    interactive?: WidgetFormInteractiveProps;
}

// ============================================================================
// Keyframe animations (injected once via <style>)
// ============================================================================
function InjectKeyframes() {
    return (
        <style>{`
            @keyframes wft-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
            @keyframes wft-pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
            @keyframes wft-shine-sweep { 0% { transform: translateX(-100%) skewX(-18deg); } 100% { transform: translateX(400%) skewX(-18deg); } }
            @keyframes wft-float-1 { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(15px, -20px); } }
            @keyframes wft-float-2 { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(-20px, 15px); } }
            @keyframes wft-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            @keyframes wft-success-pop { from { opacity: 0; transform: scale(0.95) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        `}</style>
    );
}

// ============================================================================
// Interactive input helpers — used by all templates when in interactive mode
// ============================================================================
function interactiveInputProps(
    interactive: WidgetFormInteractiveProps | undefined,
    field: string,
) {
    if (!interactive) return {};
    return {
        value: interactive.values[field] || '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => interactive.onFieldChange(field, e.target.value),
    };
}

function FieldError({ interactive, field }: { interactive?: WidgetFormInteractiveProps; field: string }) {
    if (!interactive?.fieldErrors[field]) return null;
    return <p className="px-1 pt-1 text-left text-xs text-red-500">{interactive.fieldErrors[field]}</p>;
}

function FormError({ interactive }: { interactive?: WidgetFormInteractiveProps }) {
    if (!interactive?.error) return null;
    return <p className="px-2 pt-2 text-left text-xs text-red-500">{interactive.error}</p>;
}

function SubmitButtonContent({ interactive, buttonText }: { interactive?: WidgetFormInteractiveProps; buttonText: string }) {
    if (interactive?.submitting) {
        return (
            <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Joining...
            </span>
        );
    }
    return <>{buttonText}</>;
}

function SuccessView({ interactive, isDark = false }: { interactive: WidgetFormInteractiveProps; isDark?: boolean }) {
    if (interactive.successData) {
        return (
            <div style={{ animation: 'wft-success-pop 0.4s ease-out' }}>
                <WaitlistSuccessCard
                    position={interactive.successData.position}
                    totalWaitlist={interactive.successData.totalWaitlist}
                    referralLink={interactive.successData.referralLink}
                    referralsCount={interactive.successData.referralsCount}
                    primaryColor={interactive.primaryColor || '#6366F1'}
                    successMessage={interactive.successMessage}
                    milestones={interactive.successData.milestones}
                    gamificationEnabled={interactive.successData.gamificationEnabled}
                    showQueuePosition={interactive.showQueuePosition}
                    showReferralAfterSubmit={interactive.showReferralAfterSubmit}
                />
            </div>
        );
    }
    return <SuccessOverlay interactive={interactive} isDark={isDark} />;
}

function SuccessOverlay({ interactive, isDark = false }: { interactive: WidgetFormInteractiveProps; isDark?: boolean }) {
    return (
        <div
            className={`flex flex-col items-center justify-center py-10 px-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}
            style={{ animation: 'wft-success-pop 0.4s ease-out' }}
        >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-50'}`}>
                <svg className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
            </div>
            <p className="text-lg font-semibold">{interactive.successMessage || "You're on the list!"}</p>
            <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>We&apos;ll be in touch soon.</p>
        </div>
    );
}

// ============================================================================
// 1. CENTERED CLASSIC  — "Modern SaaS"
//    Subtle gradient bg, pill badge, colored shadow, refined inputs
// ============================================================================
export function CenteredTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const inputClass = "w-full px-4 py-3.5 rounded-xl bg-gray-50/70 border-[1.5px] border-transparent text-gray-900 placeholder-gray-400 text-[15px] outline-none transition-all duration-200 hover:bg-gray-100/70 focus:border-[var(--focus-clr)] focus:bg-white";

    return (
        <div className="@container w-full" style={{ animation: 'wft-fade-in 0.4s ease-out' }}>
            <InjectKeyframes />
            <div
                className="rounded-2xl p-6 @md:p-8"
                style={{
                    background: 'linear-gradient(180deg, #ffffff 0%, #F8FAFC 100%)',
                    border: '1px solid #E8ECF1',
                    boxShadow: `0 4px 24px -4px rgba(0,0,0,0.08), 0 8px 32px -8px ${primaryColor}12`,
                    '--focus-clr': `${primaryColor}B3`,
                } as React.CSSProperties}
            >
                {interactive?.submitted ? (
                    <SuccessView interactive={interactive} />
                ) : (
                    <>
                        {/* Badge */}
                        <div className="flex justify-center mb-4">
                            <span
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                            >
                                <Icon name="SparklesIcon" className="w-3 h-3" />
                                Early Access
                            </span>
                        </div>

                        <h3 className="text-xl @md:text-2xl font-bold text-gray-900 mb-1.5 text-center tracking-tight">
                            {emailPrompt || 'Join Our Waitlist'}
                        </h3>
                        <p className="text-gray-500 text-sm text-center mb-6 leading-relaxed max-w-sm mx-auto">
                            Be the first to know when we launch. Get early access and exclusive updates.
                        </p>

                        <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="flex flex-col gap-3 max-w-md mx-auto w-full">
                            {collectFirstName && collectLastName ? (
                                <div className="flex flex-col @sm:flex-row gap-3">
                                    <div className="flex-1">
                                        <input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                        <FieldError interactive={interactive} field="firstName" />
                                    </div>
                                    <div className="flex-1">
                                        <input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                        <FieldError interactive={interactive} field="lastName" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {collectFirstName && (
                                        <div>
                                            <input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                            <FieldError interactive={interactive} field="firstName" />
                                        </div>
                                    )}
                                    {collectLastName && (
                                        <div>
                                            <input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                            <FieldError interactive={interactive} field="lastName" />
                                        </div>
                                    )}
                                </>
                            )}
                            <div>
                                <input type="email" placeholder="Enter your email" className={inputClass} {...interactiveInputProps(interactive, 'email')} />
                                <FieldError interactive={interactive} field="email" />
                            </div>
                            {collectPhone && (
                                <div>
                                    <input type="tel" placeholder="Phone Number" className={inputClass} {...interactiveInputProps(interactive, 'phone')} />
                                    <FieldError interactive={interactive} field="phone" />
                                </div>
                            )}
                            {customQuestion && (
                                <div>
                                    <input type="text" placeholder={customQuestion} className={inputClass} {...interactiveInputProps(interactive, 'customAnswer')} />
                                    <FieldError interactive={interactive} field="customAnswer" />
                                </div>
                            )}
                            <button type="submit" disabled={interactive?.submitting}
                                className="w-full px-8 py-3.5 text-white font-semibold rounded-xl transition-all text-[15px] hover:-translate-y-0.5 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ backgroundColor: primaryColor, boxShadow: `0 2px 8px ${primaryColor}40` }}
                            >
                                <SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} />
                            </button>
                            <FormError interactive={interactive} />
                        </form>

                        <p className="text-xs text-gray-400 text-center mt-4">No spam. Unsubscribe anytime.</p>
                    </>
                )}
                {!interactive?.hideChrome && (
                    <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 mt-4 pt-4 border-t border-gray-100 hover:opacity-80 transition-opacity">
                        <span className="text-[11px] text-gray-300">Powered by</span>
                        <span className="text-[11px] font-semibold text-gray-400">Waitlistly</span>
                    </a>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// 2. MINIMAL  — "Notify Me Bar"
//    Ultra-compact, email+button joined as pill, no decoration
// ============================================================================
export function MinimalTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const inputClass = "w-full px-3.5 py-2.5 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-400 text-sm outline-none transition-colors hover:border-gray-300 focus:border-gray-400";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div className="bg-white rounded-xl border border-gray-200 p-4 @md:p-5">
                {interactive?.submitted ? (
                    <SuccessView interactive={interactive} />
                ) : (
                    <>
                        <h3 className="text-base font-medium text-gray-900 mb-3 text-center">
                            {emailPrompt || 'Get notified when we launch'}
                        </h3>

                        <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="space-y-2.5">
                            {collectFirstName && collectLastName ? (
                                <div className="flex flex-col @sm:flex-row gap-2.5">
                                    <div className="flex-1">
                                        <input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                        <FieldError interactive={interactive} field="firstName" />
                                    </div>
                                    <div className="flex-1">
                                        <input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                        <FieldError interactive={interactive} field="lastName" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {collectFirstName && (
                                        <div>
                                            <input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                            <FieldError interactive={interactive} field="firstName" />
                                        </div>
                                    )}
                                    {collectLastName && (
                                        <div>
                                            <input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                            <FieldError interactive={interactive} field="lastName" />
                                        </div>
                                    )}
                                </>
                            )}
                            {collectPhone && (
                                <div>
                                    <input type="tel" placeholder="Phone Number" className={inputClass} {...interactiveInputProps(interactive, 'phone')} />
                                    <FieldError interactive={interactive} field="phone" />
                                </div>
                            )}
                            {customQuestion && (
                                <div>
                                    <input type="text" placeholder={customQuestion} className={inputClass} {...interactiveInputProps(interactive, 'customAnswer')} />
                                    <FieldError interactive={interactive} field="customAnswer" />
                                </div>
                            )}
                            {/* Joined pill: email + button */}
                            <div className="flex flex-col @sm:flex-row">
                                <div className="flex-1 min-w-0">
                                    <input type="email" placeholder="Enter your email"
                                        className="w-full px-3.5 py-2.5 rounded-lg @sm:rounded-l-lg @sm:rounded-r-none bg-white border border-gray-200 @sm:border-r-0 text-gray-900 placeholder-gray-400 text-sm outline-none transition-colors hover:border-gray-300 focus:border-gray-400"
                                        {...interactiveInputProps(interactive, 'email')}
                                    />
                                    <FieldError interactive={interactive} field="email" />
                                </div>
                                <button type="submit" disabled={interactive?.submitting}
                                    className="w-full @sm:w-auto px-5 py-2.5 text-white font-semibold rounded-lg @sm:rounded-r-lg @sm:rounded-l-none text-sm whitespace-nowrap shrink-0 transition-opacity hover:opacity-90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 mt-2 @sm:mt-0"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} />
                                </button>
                            </div>
                            <FormError interactive={interactive} />
                        </form>
                    </>
                )}

                {!interactive?.hideChrome && (
                    <div className="flex justify-end mt-2.5">
                        <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity">
                            <span className="text-[10px] text-gray-300">Powered by</span>
                            <span className="text-[10px] font-semibold text-gray-400">Waitlistly</span>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// 3. FLOATING CARD  — "Glassmorphism"
//    Gradient header with floating orbs, glass-effect form body
// ============================================================================
export function CardTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const glassInputStyle = { background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)' };
    const inputClass = "w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-400 text-[15px] outline-none transition-all duration-200 focus:border-[var(--focus-clr)]";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div
                className="rounded-3xl overflow-hidden"
                style={{
                    background: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(255,255,255,0.2)',
                }}
            >
                {/* Gradient header with floating orbs */}
                <div
                    className="px-6 @md:px-10 py-8 @md:py-10 text-center relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}DD)` }}
                >
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute top-4 left-1/4 w-40 h-40 rounded-full blur-3xl opacity-40" style={{ backgroundColor: '#fff', animation: 'wft-float-1 8s ease-in-out infinite' }} />
                        <div className="absolute bottom-4 right-1/4 w-32 h-32 rounded-full blur-3xl opacity-30" style={{ backgroundColor: '#fff', animation: 'wft-float-2 10s ease-in-out infinite' }} />
                    </div>
                    <div className="relative z-10">
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium text-white bg-white/20 backdrop-blur-sm mb-4">
                            <Icon name="SparklesIcon" className="w-3 h-3" /> Early Access
                        </span>
                        <h3 className="text-xl @md:text-2xl font-bold text-white mb-2">
                            {emailPrompt || 'Join Our Waitlist'}
                        </h3>
                        <p className="text-white/70 text-sm">Reserve your spot for early access.</p>
                    </div>
                </div>

                {/* Glass form body */}
                <div className="p-6 @md:p-8 -mt-3 rounded-t-2xl relative"
                    style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', '--focus-clr': `${primaryColor}B3` } as React.CSSProperties}
                >
                    {interactive?.submitted ? (
                        <SuccessView interactive={interactive} />
                    ) : (
                        <>
                            <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="space-y-3">
                                {collectFirstName && collectLastName ? (
                                    <div className="flex flex-col @sm:flex-row gap-3">
                                        <div className="flex-1">
                                            <input type="text" placeholder="First Name" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'firstName')} />
                                            <FieldError interactive={interactive} field="firstName" />
                                        </div>
                                        <div className="flex-1">
                                            <input type="text" placeholder="Last Name" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'lastName')} />
                                            <FieldError interactive={interactive} field="lastName" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {collectFirstName && (
                                            <div><input type="text" placeholder="First Name" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>
                                        )}
                                        {collectLastName && (
                                            <div><input type="text" placeholder="Last Name" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>
                                        )}
                                    </>
                                )}
                                <div>
                                    <input type="email" placeholder="Enter your email" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'email')} />
                                    <FieldError interactive={interactive} field="email" />
                                </div>
                                {collectPhone && (
                                    <div><input type="tel" placeholder="Phone Number" className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'phone')} /><FieldError interactive={interactive} field="phone" /></div>
                                )}
                                {customQuestion && (
                                    <div><input type="text" placeholder={customQuestion} className={inputClass} style={glassInputStyle} {...interactiveInputProps(interactive, 'customAnswer')} /><FieldError interactive={interactive} field="customAnswer" /></div>
                                )}
                                <button type="submit" disabled={interactive?.submitting}
                                    className="group relative w-full overflow-hidden px-8 py-3.5 text-white font-semibold rounded-xl transition-all text-[15px] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)` }}
                                >
                                    <span className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-white/20 transition-transform duration-700 group-hover:translate-x-[420%]" />
                                    <span className="relative"><SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} /></span>
                                </button>
                                <FormError interactive={interactive} />
                            </form>
                            <p className="text-xs text-gray-400 text-center mt-4">No spam. Unsubscribe anytime.</p>
                        </>
                    )}
                    {!interactive?.hideChrome && (
                        <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-gray-100 hover:opacity-80 transition-opacity">
                            <span className="text-[11px] text-gray-300">Powered by</span>
                            <span className="text-[11px] font-semibold text-gray-400">Waitlistly</span>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// 4. LAUNCH CARD — "Dark Neon" (Obsidian Monolith design system)
//    Dark bg, dot-grid, neon glow, shine sweep, developer noir
// ============================================================================
export function LaunchCardTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const darkInputStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' };
    const inputClass = "w-full px-4 py-3 rounded-lg text-white placeholder-white/40 text-[15px] outline-none transition-all duration-200";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div
                className="relative overflow-hidden rounded-2xl"
                style={{
                    backgroundColor: '#0E0E13',
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: `0 0 60px ${primaryColor}15, 0 0 120px ${primaryColor}08`,
                    '--focus-clr': `${primaryColor}80`,
                } as React.CSSProperties}
            >
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                <div className="absolute left-0 top-0 h-[3px] w-full" style={{ backgroundColor: primaryColor }} />

                <div className="relative p-6 @md:p-8">
                    {interactive?.submitted ? (
                        <SuccessView interactive={interactive} isDark />
                    ) : (
                        <>
                            <div className="mb-5 flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.07] px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur-sm border border-white/[0.08]">
                                    <span className="h-2 w-2 rounded-full bg-emerald-400" style={{ animation: 'wft-pulse-dot 2s infinite' }} />
                                    Private beta
                                </span>
                                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
                                    <Icon name="SparklesIcon" className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                                    Early access
                                </div>
                            </div>

                            <div className="mb-6">
                                <h3 className="text-2xl @md:text-3xl font-bold leading-tight text-white tracking-tight">
                                    {emailPrompt || 'Join Our Waitlist'}
                                </h3>
                                <p className="mt-2 text-sm leading-6 text-gray-400">
                                    Get priority access, product drops, and launch updates before the public release.
                                </p>
                            </div>

                            <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="space-y-3">
                                {collectFirstName && collectLastName ? (
                                    <div className="grid grid-cols-1 @sm:grid-cols-2 gap-3">
                                        <div><input type="text" placeholder="First Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>
                                        <div><input type="text" placeholder="Last Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>
                                    </div>
                                ) : (
                                    <>
                                        {collectFirstName && (<div><input type="text" placeholder="First Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>)}
                                        {collectLastName && (<div><input type="text" placeholder="Last Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>)}
                                    </>
                                )}
                                <div><input type="email" placeholder="Enter your email" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'email')} /><FieldError interactive={interactive} field="email" /></div>
                                {collectPhone && (<div><input type="tel" placeholder="Phone Number" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'phone')} /><FieldError interactive={interactive} field="phone" /></div>)}
                                {customQuestion && (<div><input type="text" placeholder={customQuestion} className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'customAnswer')} /><FieldError interactive={interactive} field="customAnswer" /></div>)}
                                <button type="submit" disabled={interactive?.submitting}
                                    className="group relative w-full overflow-hidden rounded-xl px-8 py-3.5 text-[15px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                    style={{ backgroundColor: primaryColor, boxShadow: `0 0 20px ${primaryColor}30` }}
                                >
                                    <span className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-white/25 transition-transform duration-700 group-hover:translate-x-[420%]" />
                                    <span className="relative"><SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} /></span>
                                </button>
                                <FormError interactive={interactive} />
                            </form>

                            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: 'wft-pulse-dot 2s infinite' }} />
                                <span>2,847 on waitlist</span>
                            </div>
                        </>
                    )}

                    <div className="mt-4 flex flex-col gap-3 border-t border-white/[0.08] pt-4 @sm:flex-row @sm:items-center @sm:justify-between">
                        <p className="text-xs text-gray-500">No spam. Unsubscribe anytime.</p>
                        {!interactive?.hideChrome && (
                            <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-300 transition-colors">
                                <span>Powered by</span>
                                <span className="font-semibold">Waitlistly</span>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// 5. SPLIT VIEW  — "Bold Split" (Kinetic Monolith design system)
//    Gradient left panel with social proof, clean form right panel
// ============================================================================
export function SplitTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const inputClass = "w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm outline-none transition-colors hover:border-gray-300 focus:border-gray-400 focus:bg-white";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
                <div className="flex flex-col @3xl:flex-row">
                    {/* Left: branded info panel */}
                    <div
                        className="@3xl:w-1/2 p-6 @md:p-8 @3xl:p-10 flex flex-col justify-center relative overflow-hidden"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)` }}
                    >
                        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                        <div className="relative z-10">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 bg-white/15 backdrop-blur-sm">
                                <Icon name="SparklesIcon" size={20} className="text-white" />
                            </div>
                            <h3 className="text-xl @md:text-2xl @3xl:text-3xl font-bold text-white mb-2 tracking-tight">
                                {emailPrompt || 'Join Our Waitlist'}
                            </h3>
                            <p className="text-white/80 text-sm leading-relaxed">
                                Be the first to know when we launch. Get early access and exclusive updates delivered to your inbox.
                            </p>
                            <div className="mt-6 flex items-center gap-3">
                                <div className="flex -space-x-2.5">
                                    {['#6366F1', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'].map((clr, i) => (
                                        <div key={i} className="w-8 h-8 rounded-full border-2 border-white/30 flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: clr }}>
                                            {['A', 'J', 'M', 'S', 'K'][i]}
                                        </div>
                                    ))}
                                </div>
                                <span className="text-xs text-white/80 font-medium">2,500+ already joined</span>
                            </div>
                        </div>
                    </div>

                    {/* Right: form panel */}
                    <div className="@3xl:w-1/2 p-6 @md:p-8 @3xl:p-10 flex flex-col justify-center">
                        {interactive?.submitted ? (
                            <SuccessView interactive={interactive} />
                        ) : (
                            <>
                                <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="space-y-4">
                                    {collectFirstName && collectLastName ? (
                                        <div className="flex flex-col @sm:flex-row gap-4">
                                            <div className="flex-1">
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">First name</label>
                                                <input type="text" placeholder="John" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                                <FieldError interactive={interactive} field="firstName" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs font-medium text-gray-500 mb-1.5">Last name</label>
                                                <input type="text" placeholder="Doe" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                                <FieldError interactive={interactive} field="lastName" />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {collectFirstName && (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">First name</label>
                                                    <input type="text" placeholder="John" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} />
                                                    <FieldError interactive={interactive} field="firstName" />
                                                </div>
                                            )}
                                            {collectLastName && (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Last name</label>
                                                    <input type="text" placeholder="Doe" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} />
                                                    <FieldError interactive={interactive} field="lastName" />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Email address</label>
                                        <input type="email" placeholder="you@company.com" className={inputClass} {...interactiveInputProps(interactive, 'email')} />
                                        <FieldError interactive={interactive} field="email" />
                                    </div>
                                    {collectPhone && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1.5">Phone number</label>
                                            <input type="tel" placeholder="+1 (555) 000-0000" className={inputClass} {...interactiveInputProps(interactive, 'phone')} />
                                            <FieldError interactive={interactive} field="phone" />
                                        </div>
                                    )}
                                    {customQuestion && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1.5">{customQuestion}</label>
                                            <input type="text" placeholder="Your answer" className={inputClass} {...interactiveInputProps(interactive, 'customAnswer')} />
                                            <FieldError interactive={interactive} field="customAnswer" />
                                        </div>
                                    )}
                                    <button type="submit" disabled={interactive?.submitting}
                                        className="group w-full px-8 py-3.5 text-white font-semibold rounded-xl transition-all text-[15px] hover:opacity-95 active:scale-[0.99] flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} />
                                        {!interactive?.submitting && (
                                            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                            </svg>
                                        )}
                                    </button>
                                    <FormError interactive={interactive} />
                                </form>
                                <p className="text-xs text-gray-400 text-center mt-3">No spam. Unsubscribe anytime.</p>
                            </>
                        )}
                        {!interactive?.hideChrome && (
                            <div className="flex justify-center mt-3 pt-3 border-t border-gray-50">
                                <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity">
                                    <span className="text-[10px] text-gray-300">Powered by</span>
                                    <span className="text-[10px] font-semibold text-gray-400">Waitlistly</span>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// 6. DARK PREMIUM — "Luxury Dark"
//    Near-black, serif heading, noise texture, refined borders, luxury feel
// ============================================================================
export function DarkPremiumTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const darkInputStyle = { backgroundColor: '#14141A', border: '1px solid #2A2A35', color: '#fff' };
    const inputClass = "w-full px-4 py-3 rounded-lg text-white text-[15px] outline-none transition-all duration-200";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div
                className="relative rounded-2xl p-6 @md:p-8 overflow-hidden"
                style={{ backgroundColor: '#09090B', border: '1px solid #1C1C22', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
            >
                <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: `${primaryColor}33` }} />
                <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }}
                />

                {interactive?.submitted ? (
                    <div className="relative z-10"><SuccessView interactive={interactive} isDark /></div>
                ) : (
                    <>
                        <div className="relative z-10 text-center">
                            <p className="text-[11px] font-medium tracking-[0.1em] uppercase mb-4" style={{ color: '#8A8A9A' }}>
                                Exclusive Early Access
                            </p>
                            <h3 className="text-xl @md:text-2xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                                {emailPrompt || 'Join Our Waitlist'}
                            </h3>
                            <div className="mx-auto mb-6 w-10 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)` }} />
                        </div>

                        <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="relative z-10 space-y-3 max-w-sm mx-auto">
                            {collectFirstName && collectLastName ? (
                                <div className="flex flex-col @sm:flex-row gap-3">
                                    <div className="flex-1"><input type="text" placeholder="First Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>
                                    <div className="flex-1"><input type="text" placeholder="Last Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>
                                </div>
                            ) : (
                                <>
                                    {collectFirstName && (<div><input type="text" placeholder="First Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>)}
                                    {collectLastName && (<div><input type="text" placeholder="Last Name" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>)}
                                </>
                            )}
                            <div><input type="email" placeholder="Enter your email" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'email')} /><FieldError interactive={interactive} field="email" /></div>
                            {collectPhone && (<div><input type="tel" placeholder="Phone Number" className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'phone')} /><FieldError interactive={interactive} field="phone" /></div>)}
                            {customQuestion && (<div><input type="text" placeholder={customQuestion} className={inputClass} style={darkInputStyle} {...interactiveInputProps(interactive, 'customAnswer')} /><FieldError interactive={interactive} field="customAnswer" /></div>)}
                            <button type="submit" disabled={interactive?.submitting}
                                className="w-full px-8 py-3.5 text-white font-semibold rounded-lg text-sm tracking-[0.08em] uppercase transition-all hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ backgroundColor: primaryColor }}
                            >
                                <SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join Waitlist'} />
                            </button>
                            <FormError interactive={interactive} />
                        </form>

                        <div className="relative z-10 mt-5 text-center">
                            <p className="text-xs" style={{ color: '#444450' }}>No spam. Unsubscribe anytime.</p>
                        </div>
                    </>
                )}

                {!interactive?.hideChrome && (
                    <div className="relative z-10 text-center mt-3">
                        <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                            <span className="text-[11px]" style={{ color: '#444450' }}>Powered by</span>
                            <span className="text-[11px] font-semibold" style={{ color: '#555560' }}>Waitlistly</span>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// 7. SOCIAL PROOF — "Trust-Driven"
//    Avatar stack, live counter, testimonial, scrolling ticker
// ============================================================================
export function SocialProofTemplate({
    primaryColor, buttonText, emailPrompt,
    collectFirstName, collectLastName, collectPhone, customQuestion, interactive,
}: WidgetFormTemplateProps) {
    const AVATARS = [
        { initial: 'A', bg: '#6366F1' },
        { initial: 'J', bg: '#8B5CF6' },
        { initial: 'M', bg: '#3B82F6' },
        { initial: 'S', bg: '#10B981' },
        { initial: 'K', bg: '#F59E0B' },
    ];
    const inputClass = "w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm outline-none transition-colors hover:border-gray-300 focus:border-gray-400";

    return (
        <div className="@container w-full">
            <InjectKeyframes />
            <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
                <div className="p-6 @md:p-8">
                    {interactive?.submitted ? (
                        <SuccessView interactive={interactive} />
                    ) : (
                        <>
                            {/* Social proof section */}
                            <div className="text-center mb-5">
                                <div className="flex items-center justify-center gap-3 mb-3">
                                    <div className="flex -space-x-2">
                                        {AVATARS.map((a, i) => (
                                            <div key={i} className="w-8 h-8 rounded-full ring-2 ring-white flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: a.bg }}>
                                                {a.initial}
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-sm font-bold text-gray-700">+2,847</span>
                                </div>
                                <div className="flex items-center justify-center gap-1.5 mb-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" style={{ animation: 'wft-pulse-dot 2s infinite' }} />
                                    <span className="text-sm font-medium text-gray-600">2,847 people have joined</span>
                                </div>
                                <div className="flex items-center justify-center gap-1.5">
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>
                                    <span className="text-xs text-gray-500">4.9/5 from early users</span>
                                </div>
                            </div>

                            {/* Testimonial */}
                            <div className="mb-6 max-w-sm mx-auto rounded-lg bg-gray-50 p-4" style={{ borderLeft: `3px solid ${primaryColor}` }}>
                                <p className="text-sm text-gray-500 italic leading-relaxed">
                                    &ldquo;This is exactly what I&apos;ve been waiting for! The early access has been incredible.&rdquo;
                                </p>
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center text-[8px] font-bold text-white">S</div>
                                    <span className="text-xs text-gray-400">Sarah M. &middot; Product Designer</span>
                                </div>
                            </div>

                            <h3 className="text-lg @md:text-xl font-bold text-gray-900 mb-4 text-center">
                                {emailPrompt || 'Join Our Waitlist'}
                            </h3>

                            <form onSubmit={interactive?.onFormSubmit || ((e) => e.preventDefault())} className="space-y-3 max-w-sm mx-auto">
                                {collectFirstName && collectLastName ? (
                                    <div className="flex flex-col @sm:flex-row gap-3">
                                        <div className="flex-1"><input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>
                                        <div className="flex-1"><input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>
                                    </div>
                                ) : (
                                    <>
                                        {collectFirstName && (<div><input type="text" placeholder="First Name" className={inputClass} {...interactiveInputProps(interactive, 'firstName')} /><FieldError interactive={interactive} field="firstName" /></div>)}
                                        {collectLastName && (<div><input type="text" placeholder="Last Name" className={inputClass} {...interactiveInputProps(interactive, 'lastName')} /><FieldError interactive={interactive} field="lastName" /></div>)}
                                    </>
                                )}
                                <div><input type="email" placeholder="Enter your email" className={inputClass} {...interactiveInputProps(interactive, 'email')} /><FieldError interactive={interactive} field="email" /></div>
                                {collectPhone && (<div><input type="tel" placeholder="Phone Number" className={inputClass} {...interactiveInputProps(interactive, 'phone')} /><FieldError interactive={interactive} field="phone" /></div>)}
                                {customQuestion && (<div><input type="text" placeholder={customQuestion} className={inputClass} {...interactiveInputProps(interactive, 'customAnswer')} /><FieldError interactive={interactive} field="customAnswer" /></div>)}
                                <button type="submit" disabled={interactive?.submitting}
                                    className="w-full px-8 py-3.5 text-white font-semibold rounded-xl transition-all text-[15px] hover:opacity-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <SubmitButtonContent interactive={interactive} buttonText={buttonText || 'Join 2,847+ others'} />
                                </button>
                                <FormError interactive={interactive} />
                                <p className="text-[10px] text-gray-400 text-center">Join 2,847+ others who already signed up</p>
                            </form>
                        </>
                    )}

                    {!interactive?.hideChrome && (
                        <a href="https://waitlistly.live" target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-gray-100 hover:opacity-80 transition-opacity">
                            <span className="text-[11px] text-gray-300">Powered by</span>
                            <span className="text-[11px] font-semibold text-gray-400">Waitlistly</span>
                        </a>
                    )}
                </div>

                {/* Activity ticker */}
                <div className="border-t border-gray-100 py-2 overflow-hidden">
                    <div className="flex gap-8 whitespace-nowrap text-[11px] text-gray-400"
                        style={{ animation: 'wft-ticker 20s linear infinite', width: 'max-content' }}
                    >
                        <span>Sarah from NYC joined 2m ago</span>
                        <span>&middot;</span>
                        <span>Mike from London joined 5m ago</span>
                        <span>&middot;</span>
                        <span>Emma from Berlin joined 8m ago</span>
                        <span>&middot;</span>
                        <span>Alex from Tokyo joined 12m ago</span>
                        <span>&middot;</span>
                        <span>Sarah from NYC joined 2m ago</span>
                        <span>&middot;</span>
                        <span>Mike from London joined 5m ago</span>
                        <span>&middot;</span>
                        <span>Emma from Berlin joined 8m ago</span>
                        <span>&middot;</span>
                        <span>Alex from Tokyo joined 12m ago</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
